import { Fragment, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { toast } from 'sonner';
import { StatusBadge } from '../components/StatusBadge.js';
import { CorrectPanel, RejectPanel } from '../components/TimesheetActions.js';
import { Button } from '../components/ui/button.js';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table.js';
import { requireRole } from '../lib/auth-guard.js';
import { trpc } from '../lib/trpc.js';

export const Route = createFileRoute('/client/timesheets')({
  component: ClientTimesheets,
  beforeLoad: async ({ context }) => {
    await requireRole(context.queryClient, ['client']);
  },
});

type PanelState = { id: string; kind: 'reject' | 'correct' } | null;

function ClientTimesheets() {
  const utils = trpc.useUtils();
  const list = trpc.timesheets.listMineAsClient.useQuery();
  const [panel, setPanel] = useState<PanelState>(null);

  const review = trpc.timesheets.reviewMine.useMutation({
    onSuccess: () => {
      utils.timesheets.listMineAsClient.invalidate();
      setPanel(null);
      toast.success('Stundenzettel bearbeitet.');
    },
    onError: (e) => toast.error(e.message),
  });
  const correct = trpc.timesheets.correctMine.useMutation({
    onSuccess: () => {
      utils.timesheets.listMineAsClient.invalidate();
      setPanel(null);
      toast.success('Korrektur gesendet — der Mitarbeiter bestätigt sie mit einem Klick.');
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <div className="min-h-screen p-6 max-w-4xl mx-auto space-y-6">
      <Link to="/client" className="text-sm text-[var(--muted-foreground)] hover:text-foreground">
        ← Zurück zur Übersicht
      </Link>
      <h1 className="text-3xl font-bold">Stundenfreigabe</h1>

      <Card>
        <CardHeader>
          <CardTitle>Eingereichte Stunden</CardTitle>
          <CardDescription>
            Bestätigen Sie die geleisteten Stunden Ihrer überlassenen Mitarbeiter — oder
            korrigieren Sie sie direkt, dann bestätigt der Mitarbeiter Ihre Korrektur mit einem
            Klick. Nach der Bestätigung gibt HireBotIQ die Abrechnung final frei.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Mitarbeiter</TableHead>
                <TableHead>Zeitraum</TableHead>
                <TableHead>Stunden</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Aktion</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.isLoading && (
                <TableRow>
                  <TableCell colSpan={5} className="text-[var(--muted-foreground)]">
                    Lade …
                  </TableCell>
                </TableRow>
              )}
              {list.data?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-[var(--muted-foreground)]">
                    Keine Stundenzettel.
                  </TableCell>
                </TableRow>
              )}
              {list.data?.map((t) => (
                <Fragment key={t.id}>
                  <TableRow>
                    <TableCell>
                      {[t.workerFirstName, t.workerLastName].filter(Boolean).join(' ') || '—'}
                    </TableCell>
                    <TableCell className="text-xs text-[var(--muted-foreground)]">
                      {t.periodStart} – {t.periodEnd}
                    </TableCell>
                    <TableCell>
                      {t.status === 'client_corrected' && t.correctedTotalHours ? (
                        <span>
                          <s className="text-[var(--muted-foreground)]">{t.originalTotalHours}</s>{' '}
                          → {t.correctedTotalHours}
                        </span>
                      ) : (
                        (t.totalHours ?? '—')
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={t.status} />
                    </TableCell>
                    <TableCell>
                      {t.status === 'submitted' && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            disabled={review.isPending}
                            onClick={() =>
                              review.mutate({ timesheetId: t.id, action: 'approve' })
                            }
                          >
                            Bestätigen
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setPanel({ id: t.id, kind: 'correct' })}
                          >
                            Korrigieren
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setPanel({ id: t.id, kind: 'reject' })}
                          >
                            Ablehnen
                          </Button>
                        </div>
                      )}
                      {t.status === 'client_corrected' && (
                        <span className="text-xs text-[var(--muted-foreground)]">
                          Wartet auf Bestätigung des Mitarbeiters
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                  {panel?.id === t.id && (
                    <TableRow>
                      <TableCell colSpan={5}>
                        {panel.kind === 'reject' ? (
                          <RejectPanel
                            pending={review.isPending}
                            onCancel={() => setPanel(null)}
                            onSubmit={(category, notes) =>
                              review.mutate({
                                timesheetId: t.id,
                                action: 'reject',
                                category,
                                notes,
                              })
                            }
                          />
                        ) : (
                          <CorrectPanel
                            pending={correct.isPending}
                            currentHours={t.totalHours}
                            onCancel={() => setPanel(null)}
                            onSubmit={(correctedTotalHours, notes) =>
                              correct.mutate({ timesheetId: t.id, correctedTotalHours, notes })
                            }
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
